class CartRecommendations extends HTMLElement {
  constructor() {
    super();

    this.abortController = null;
    this.products = [];
    this.viewedRecommendationKey = "";
    this.onCartChange = this.handleCartChange.bind(this);
    this.onResize = this.render.bind(this);
  }

  connectedCallback() {
    this.abortController = new AbortController();
    this.addEventListener("click", this.handleClick.bind(this), { signal: this.abortController.signal });
    document.addEventListener("cart:change", this.onCartChange, { signal: this.abortController.signal });
    window.addEventListener("resize", this.onResize, { signal: this.abortController.signal });
    this.load();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  get listElement() {
    return this.querySelector(".cart-recommendations__list");
  }

  get cartProductIds() {
    return new Set((this.dataset.cartProductIds || "").split(",").filter(Boolean).map((id) => Number(id)));
  }

  get visibleLimit() {
    return window.matchMedia("(max-width: 699px)").matches ? 2 : Number(this.dataset.limit || 4);
  }

  async load() {
    const productId = this.dataset.productId;

    if (!productId || !this.listElement) {
      this.remove();
      return;
    }

    try {
      const url = new URL(`${Shopify.routes.root}recommendations/products.json`, window.location.origin);
      url.searchParams.set("product_id", productId);
      url.searchParams.set("limit", this.dataset.limit || "4");
      url.searchParams.set("intent", this.dataset.intent || "related");

      const response = await fetch(url.toString(), { priority: "low" });

      if (!response.ok) {
        this.remove();
        return;
      }

      const recommendations = await response.json();
      const cartProductIds = this.cartProductIds;

      this.products = (recommendations.products || []).filter((product) => {
        const variant = this.firstAvailableVariant(product);
        return product.available && variant && !cartProductIds.has(Number(product.id));
      });

      this.render();
    } catch (error) {
      this.remove();
    }
  }

  render() {
    if (!this.listElement) {
      return;
    }

    const visibleProducts = this.products.slice(0, this.visibleLimit);

    if (visibleProducts.length === 0) {
      this.remove();
      return;
    }

    this.listElement.innerHTML = visibleProducts.map((product, index) => this.renderProduct(product, index)).join("");
    this.hidden = false;

    const viewKey = `${this.dataset.productId}:${visibleProducts.map((product) => product.id).join(",")}`;

    if (viewKey !== this.viewedRecommendationKey) {
      this.viewedRecommendationKey = viewKey;
      this.publish("cart_recommendation_view", {
        source_product_id: Number(this.dataset.productId),
        recommended_product_ids: visibleProducts.map((product) => product.id)
      });
    }
  }

  renderProduct(product, index) {
    const variant = this.firstAvailableVariant(product);
    const image = this.productImage(product);
    const title = this.escapeHtml(product.title);
    const url = `${Shopify.routes.root}products/${product.handle}`;
    const price = this.formatMoney(variant.price || product.price);
    const compareAtPrice = Number(variant.compare_at_price || product.compare_at_price || 0);
    const currentPrice = Number(variant.price || product.price || 0);
    const compareAtMarkup = compareAtPrice > currentPrice ? `<compare-at-price class="text-subdued line-through"><span class="sr-only">Regular price</span>${this.formatMoney(compareAtPrice)}</compare-at-price>` : "";
    const imageMarkup = image ? `<img src="${this.resizeImage(image, 500)}" srcset="${this.resizeImage(image, 300)} 300w, ${this.resizeImage(image, 500)} 500w, ${this.resizeImage(image, 700)} 700w" sizes="(max-width: 699px) 75vw, 25vw" alt="${title}" loading="lazy" class="cart-recommendations__image product-card__image product-card__image--primary aspect-natural" draggable="false">` : "";

    return `
      <product-card class="cart-recommendations__card product-card" handle="${this.escapeHtml(product.handle)}" role="listitem" data-product-id="${product.id}">
        <div class="product-card__figure">
          <a href="${url}" class="cart-recommendations__media product-card__media" data-cart-recommendation-link data-product-id="${product.id}" data-variant-id="${variant.id}">
            ${imageMarkup}
          </a>
        </div>

        <div class="cart-recommendations__info product-card__info">
          <div class="v-stack justify-items-center gap-1">
            <a href="${url}" class="product-title" data-cart-recommendation-link data-product-id="${product.id}" data-variant-id="${variant.id}">
              ${title}
            </a>

            <price-list class="price-list">
              <sale-price class="${compareAtPrice > currentPrice ? "text-on-sale" : "text-subdued"}">
                <span class="sr-only">Sale price</span>${price}
              </sale-price>
              ${compareAtMarkup}
            </price-list>
          </div>

          <button type="button" class="button button--outline button--sm cart-recommendations__add" data-cart-recommendation-add data-product-id="${product.id}" data-variant-id="${variant.id}" aria-label="Add ${title} to cart">
            Add
          </button>
        </div>
      </product-card>
    `;
  }

  async handleClick(event) {
    const link = event.target.closest("[data-cart-recommendation-link]");
    const addButton = event.target.closest("[data-cart-recommendation-add]");

    if (link) {
      this.publish("cart_recommendation_click", this.eventPayload(link));
      return;
    }

    if (!addButton || addButton.getAttribute("aria-busy") === "true") {
      return;
    }

    this.publish("cart_recommendation_click", this.eventPayload(addButton));
    await this.addVariant(addButton);
  }

  async addVariant(button) {
    button.setAttribute("aria-busy", "true");
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    const sectionsToBundle = [];
    const sectionId = this.sectionIdFromElement(this.closest(".shopify-section"));
    const mainCartSectionId = this.sectionIdFromElement(document.querySelector(".shopify-section--main-cart"));

    if (sectionId) {
      sectionsToBundle.push(sectionId);
    }

    if (mainCartSectionId && mainCartSectionId !== sectionId) {
      sectionsToBundle.push(mainCartSectionId);
    }

    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
          sections: [...new Set(sectionsToBundle)].join(","),
          sections_url: window.location.pathname
        })
      });

      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(responseJson.description || responseJson.message || "Unable to add product to cart.");
      }

      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent.sections = responseJson.sections || {};

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "variant:add",
            onSuccessDo: "stay_on_cart",
            cart: cartContent
          }
        })
      );

      this.publish("cart_recommendation_add", {
        ...this.eventPayload(button),
        cart_item_count: cartContent.item_count
      });

      if (window.themeVariables?.settings?.pageType === "cart" && mainCartSectionId && cartContent.sections?.[mainCartSectionId]) {
        const mainCartSection = document.getElementById(`shopify-section-${mainCartSectionId}`);

        if (mainCartSection) {
          mainCartSection.outerHTML = cartContent.sections[mainCartSectionId];
        }
      } else if (window.themeVariables?.settings?.pageType === "cart" && sectionId && cartContent.sections?.[sectionId]) {
        const currentSection = document.getElementById(`shopify-section-${sectionId}`);

        if (currentSection) {
          currentSection.outerHTML = cartContent.sections[sectionId];
        }
      }
    } catch (error) {
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      const errorAnnouncement = document.getElementById("error-announcement");

      if (errorAnnouncement) {
        errorAnnouncement.textContent = error.message;
      }
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      button.removeAttribute("aria-busy");
    }
  }

  handleCartChange(event) {
    const cart = event.detail?.cart;

    if (!cart?.items?.length) {
      this.remove();
      return;
    }

    this.dataset.productId = cart.items[0].product_id;
    this.dataset.cartProductIds = cart.items.map((item) => item.product_id).join(",");
    this.load();
  }

  firstAvailableVariant(product) {
    return (product.variants || []).find((variant) => variant.available) || null;
  }

  productImage(product) {
    const image = product.featured_image || product.images?.[0] || "";

    if (typeof image === "string") {
      return image;
    }

    return image?.src || image?.url || "";
  }

  sectionIdFromElement(element) {
    return element?.id?.replace("shopify-section-", "") || "";
  }

  eventPayload(element) {
    return {
      source_product_id: Number(this.dataset.productId),
      recommended_product_id: Number(element.dataset.productId),
      recommended_variant_id: Number(element.dataset.variantId)
    };
  }

  publish(eventName, payload) {
    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish(eventName, payload);
    }

    document.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
  }

  formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    const currency = window.Shopify?.currency?.active || "USD";
    const locale = window.Shopify?.locale || document.documentElement.lang || "en";

    try {
      return new Intl.NumberFormat(locale, { style: "currency", currency }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  resizeImage(imageUrl, width) {
    if (!imageUrl) {
      return "";
    }

    const separator = imageUrl.includes("?") ? "&" : "?";
    return `${imageUrl}${separator}width=${width}`;
  }

  escapeHtml(value) {
    return String(value || "").replace(/[&<>"']/g, (character) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;"
    })[character]);
  }
}

if (!window.customElements.get("cart-recommendations")) {
  window.customElements.define("cart-recommendations", CartRecommendations);
}
